import { useState, useEffect } from 'react';
import { track } from '@/lib/analytics';

const DOT_COLORS = {
  cool: '#22c55e',
  warm: '#eab308',
  hot: '#ef4444',
  loading: '#9CA3AF',
} as const;

type TempCheckBarProps = {
  level?: 'cool' | 'warm' | 'hot';
  label?: string;
  suggestion?: string | null;
  isLoading: boolean;
  tooShort?: boolean;
  onUseSuggestion?: (suggestion: string) => void;
};

export function TempCheckBar({
  level,
  label,
  suggestion,
  isLoading,
  tooShort,
  onUseSuggestion,
}: TempCheckBarProps) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [level]);

  const effectiveLevel = level ?? 'cool';
  const dotColor =
    isLoading || tooShort ? DOT_COLORS.loading : DOT_COLORS[effectiveLevel];
  const hasSuggestion =
    !isLoading && (level === 'warm' || level === 'hot') && suggestion;

  return (
    <div className='px-4 pb-2'>
      <button
        type='button'
        className='flex items-center gap-2 w-full text-left'
        onClick={() => {
          if (!hasSuggestion) return;
          setExpanded((e) => {
            if (!e) track('tone_check_rewrite_viewed', { level });
            return !e;
          });
        }}
        disabled={!hasSuggestion}
      >
        <span
          className={`w-2 h-2 rounded-full shrink-0 ${isLoading ? 'animate-pulse' : ''}`}
          style={{ backgroundColor: dotColor }}
        />
        <span className='flex-1 text-[13px] font-semibold text-gray-400'>
          {isLoading
            ? 'Checking temp\u2026'
            : tooShort
              ? 'Keep typing to check temp'
              : label ?? 'Tone looks good'}
        </span>
        {hasSuggestion && (
          <span className='px-2.5 py-0.5 rounded-[10px] bg-yellow-500/10 text-[11px] font-bold text-amber-700'>
            {expanded ? 'Hide' : 'See rewrite'}
          </span>
        )}
      </button>

      {hasSuggestion && (
        <div
          className={`overflow-hidden transition-all duration-200 ${
            expanded ? 'max-h-20 opacity-100 mt-1.5' : 'max-h-0 opacity-0'
          }`}
        >
          <div className='flex items-center gap-2 pl-4'>
            <span className='flex-1 text-xs text-gray-500 italic line-clamp-2'>
              {suggestion}
            </span>
            <button
              type='button'
              className='shrink-0 px-2.5 py-1 rounded-[10px] bg-primary/10 text-xs font-bold text-primary hover:bg-primary/20 transition-colors'
              onClick={() => onUseSuggestion?.(suggestion!)}
            >
              Use this instead
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
